import { Home, Plus, MessageCircle, Bell, User, Image as ImageIcon, Backpack, Users, Heart, Shield, Search } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Bottom navigation order (LTR):
// feed | mine | swipe (heart) | + (create, primary) | posts | chats | profile
// User request: place swipe "between + and mine icon" and ensure the Like
// (heart) icon is visible without hunting for it in the top-right cluster.
const itemsConfig = [
  { to: "/feed", icon: Home, key: "feed", testid: "nav-feed" },
  { to: "/mine", icon: Backpack, key: "mine", testid: "nav-mine" },
  { to: "/swipe", icon: Heart, key: "swipe", testid: "nav-swipe" },
  { to: "/create", icon: Plus, key: "create", testid: "nav-create", primary: true },
  { to: "/posts", icon: ImageIcon, key: "posts", testid: "nav-posts" },
  { to: "/chats", icon: MessageCircle, key: "chats", testid: "nav-chats" },
  { to: "/profile", icon: User, key: "you", testid: "nav-profile" },
];

export default function BottomNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setUnread(data.filter((n) => !n.read).length);
      } catch (_) { /* silently ignore poll errors */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  // Intentionally no deps — start once when component mounts, persist for lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showTopBar = ["/feed", "/posts", "/chats", "/search"].includes(pathname);

  return (
    <>
      {showTopBar && (
        <div className="fixed top-3 right-3 sm:right-[calc(50%-225px)] z-40 flex items-center gap-1.5 max-w-[calc(100vw-1rem)]">
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#FF7A6B] text-white shadow-soft flex items-center justify-center hover:-translate-y-0.5 hover:shadow-soft-md transition-all border-2 border-ink shrink-0"
              aria-label="Admin Panel"
            >
              <Shield className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2.25} />
            </NavLink>
          )}
          <NavLink
            to="/search"
            data-testid="nav-search"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full glass-strong shadow-soft flex items-center justify-center hover:-translate-y-0.5 hover:shadow-soft-md transition-all shrink-0"
            aria-label="Search Users"
          >
            <Search className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#00CED1]" strokeWidth={2.5} />
          </NavLink>
          <NavLink
            to="/matches"
            data-testid="nav-matches"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full glass-strong shadow-soft flex items-center justify-center hover:-translate-y-0.5 hover:shadow-soft-md transition-all shrink-0"
            aria-label="Matches"
          >
            <Heart className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#00CED1]" strokeWidth={2.5} fill="#00CED1" />
          </NavLink>
          <NavLink
            to="/friends"
            data-testid="nav-friends"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full glass-strong shadow-soft flex items-center justify-center hover:-translate-y-0.5 hover:shadow-soft-md transition-all shrink-0"
          >
            <Users className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2.25} />
          </NavLink>
          <NavLink
            to="/notifications"
            data-testid="nav-notifications"
            className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full glass-strong shadow-soft flex items-center justify-center hover:-translate-y-0.5 hover:shadow-soft-md transition-all shrink-0"
          >
            <Bell className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2.25} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 bg-gradient-to-br from-[#FF7A6B] to-[#FF5C49] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-glow-coral" data-testid="notif-badge">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </NavLink>
        </div>
      )}
      <nav
        className="fixed bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-1rem)] max-w-md sm:max-w-lg md:max-w-xl lg:max-w-3xl z-40 glass-strong rounded-full p-1 sm:p-1.5 lg:p-2 flex items-center justify-between shadow-soft-md"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {itemsConfig.map(({ to, icon: Icon, key, testid, primary }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center px-1 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full flex-1 min-w-0 transition-all ${
                primary
                  ? "bg-gradient-to-br from-[#5EEAD4] via-[#14C8B0] to-[#0F9D8F] text-white px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 lg:py-3.5 -mt-4 sm:-mt-5 lg:-mt-6 shadow-glow hover:-translate-y-0.5 shrink-0"
                  : isActive
                  ? "bg-[#0F1115] text-white"
                  : "text-[#4A4F58] hover:text-[#0F1115] hover:bg-black/5"
              }`
            }
          >
            <Icon className={primary ? "w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" : "w-[17px] h-[17px] sm:w-5 sm:h-5 lg:w-[26px] lg:h-[26px]"} strokeWidth={2.25} />
            <span className={`text-[9px] sm:text-xs lg:text-[13px] mt-0.5 lg:mt-1 font-semibold tracking-tight truncate max-w-[48px] sm:max-w-full text-center leading-none ${primary ? "hidden" : "block"}`}>{t(`nav.${key}`)}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
